import Link from 'next/link';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { MarketingLandingCopy } from '@/i18n/marketing-landing-copy';
import { MarketingSquare } from './section-label';
import { getMobileFlatLinks, getMobileNavSections } from './nav-menu-model';

/**
 * Mobile menu contents.
 *
 * Top-level solution and resource groups expand in place. Direct routes remain
 * flat rows between them so the order matches the desktop navigation.
 */
export function MobileNavPanel({
  copy,
  onNavigate,
}: {
  copy: MarketingLandingCopy;
  onNavigate: () => void;
}) {
  const sections = getMobileNavSections(copy);
  const flatLinks = getMobileFlatLinks(copy);
  const [solutionsSection, resourcesSection] = sections;

  return (
    <Accordion type="multiple" className="pb-16">
      {solutionsSection ? (
        <MobileNavSection
          value="solutions"
          section={solutionsSection}
          onNavigate={onNavigate}
        />
      ) : null}

      <ul>
        {flatLinks.map((link) => (
          <li key={link.label} className="border-marketing-border border-b">
            <Link
              href={link.href}
              onClick={onNavigate}
              className="bg-marketing-bg text-marketing-fg focus-visible:outline-marketing-accent-strong flex items-center justify-between px-4 py-3 text-[clamp(0.875rem,1.1vw,1rem)] leading-[1.25] font-bold [letter-spacing:-0.02em] outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
            >
              {link.label}
              <MarketingSquare size="sm" />
            </Link>
          </li>
        ))}
      </ul>

      {resourcesSection ? (
        <MobileNavSection
          value="resources"
          section={resourcesSection}
          onNavigate={onNavigate}
        />
      ) : null}
    </Accordion>
  );
}

function MobileNavSection({
  onNavigate,
  section,
  value,
}: {
  onNavigate: () => void;
  section: ReturnType<typeof getMobileNavSections>[number];
  value: string;
}) {
  return (
    <AccordionItem value={value} className="border-marketing-border border-b">
      <AccordionTrigger className="bg-marketing-panel text-marketing-panel-fg focus-visible:outline-marketing-accent-strong rounded-none px-4 py-3 text-[clamp(0.875rem,1.1vw,1rem)] leading-[1.25] font-bold [letter-spacing:-0.02em] outline-none hover:no-underline focus-visible:ring-0 focus-visible:outline-2 focus-visible:-outline-offset-2 [&>svg]:text-current">
        {section.title}
      </AccordionTrigger>
      <AccordionContent className="pb-0">
        <ul>
          {section.links.map((link) => (
            <li key={link.label}>
              <Link
                href={link.href}
                onClick={onNavigate}
                className="bg-marketing-accent-subtle text-marketing-accent-fg border-marketing-bg focus-visible:outline-marketing-accent-strong block border-b px-4 py-3.5 text-sm font-medium [letter-spacing:-0.01em] outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </AccordionContent>
    </AccordionItem>
  );
}
